import React, { useState, useEffect, useCallback } from 'react';
import { Activity, Globe, User, Filter, Search, Trash2, RefreshCw, Monitor, Clock, MapPin, MousePointer, LogIn, CreditCard, BookOpen, Languages, MessageSquare, Eye, Share2, Zap, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '@/config';

const ACTION_CONFIG = {
  page_view: { icon: Eye, color: 'text-blue-500', label: 'زيارة صفحة', labelEn: 'Page View' },
  subscribe_click: { icon: CreditCard, color: 'text-emerald-600', label: 'محاولة اشتراك', labelEn: 'Subscribe Click' },
  test_start: { icon: BookOpen, color: 'text-indigo-500', label: 'بدء اختبار', labelEn: 'Test Start' },
  test_complete: { icon: BookOpen, color: 'text-green-600', label: 'إنهاء اختبار', labelEn: 'Test Complete' },
  login: { icon: LogIn, color: 'text-cyan-500', label: 'تسجيل دخول', labelEn: 'Login' },
  register: { icon: User, color: 'text-purple-500', label: 'تسجيل جديد', labelEn: 'Register' },
  language_change: { icon: Languages, color: 'text-amber-500', label: 'تغيير لغة', labelEn: 'Language Change' },
  ai_chat: { icon: MessageSquare, color: 'text-pink-500', label: 'محادثة AI', labelEn: 'AI Chat' },
  sign_view: { icon: Eye, color: 'text-teal-500', label: 'عرض إشارة', labelEn: 'Sign View' },
  lesson_view: { icon: BookOpen, color: 'text-orange-500', label: 'عرض درس', labelEn: 'Lesson View' },
  share_result: { icon: Share2, color: 'text-blue-600', label: 'مشاركة نتيجة', labelEn: 'Share Result' },
  speed_challenge: { icon: Zap, color: 'text-yellow-500', label: 'تحدي السرعة', labelEn: 'Speed Challenge' },
  js_error: { icon: AlertTriangle, color: 'text-red-500', label: 'خطأ', labelEn: 'JS Error' },
  promise_error: { icon: AlertTriangle, color: 'text-red-400', label: 'خطأ', labelEn: 'Error' },
  error: { icon: AlertTriangle, color: 'text-red-500', label: 'خطأ', labelEn: 'Error' },
};

const ActivityLogs = ({ token, i18n }) => {
  const [activities, setActivities] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterAction, setFilterAction] = useState('all');
  const [filterIP, setFilterIP] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [actionTypes, setActionTypes] = useState([]);
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const isAr = i18n?.language === 'ar';

  const fetchData = useCallback(async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const params = new URLSearchParams({ limit: '50', skip: String(page * 50) });
      if (filterAction !== 'all') params.append('action', filterAction);
      if (filterIP) params.append('ip', filterIP);

      const [activitiesRes, summaryRes] = await Promise.all([
        axios.get(`${API}/admin/user-activities?${params}`, { headers }),
        axios.get(`${API}/admin/user-activities/summary`, { headers })
      ]);

      setActivities(activitiesRes.data.activities || []);
      setTotal(activitiesRes.data.total || 0);
      setActionTypes(activitiesRes.data.action_types || []);
      setSummary(summaryRes.data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  }, [token, filterAction, filterIP, page]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleCleanup = async () => {
    if (!window.confirm(isAr ? 'حذف كل سجلات الأنشطة؟' : 'Delete all activity logs?')) return;
    try {
      await axios.delete(`${API}/admin/user-activities/cleanup`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(isAr ? 'تم الحذف' : 'Cleaned up');
      fetchData();
    } catch { toast.error('Error'); }
  };

  const formatTime = (ts) => {
    if (!ts) return '-';
    const d = new Date(ts);
    return d.toLocaleDateString('sv-SE') + ' ' + d.toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' });
  };

  const getActionInfo = (action) => ACTION_CONFIG[action] || { icon: Activity, color: 'text-slate-500', label: action, labelEn: action };

  const filtered = searchTerm
    ? activities.filter(a =>
        (a.user_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (a.user_email || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (a.ip_address || '').includes(searchTerm) ||
        (a.details || '').toLowerCase().includes(searchTerm.toLowerCase())
      )
    : activities;

  if (loading) return <div className="flex justify-center p-8"><div className="w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-4" data-testid="user-activities-panel">
      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="bg-white dark:bg-slate-800 rounded-xl p-4 border border-slate-200 dark:border-slate-700">
            <div className="flex items-center gap-2 mb-1">
              <Activity className="w-4 h-4 text-blue-500" />
              <span className="text-xs text-slate-500">{isAr ? 'إجمالي الأنشطة' : 'Total Activities'}</span>
            </div>
            <p className="text-2xl font-bold text-slate-900 dark:text-white">{summary.total_activities}</p>
          </div>
          <div className="bg-white dark:bg-slate-800 rounded-xl p-4 border border-slate-200 dark:border-slate-700">
            <div className="flex items-center gap-2 mb-1">
              <Clock className="w-4 h-4 text-emerald-500" />
              <span className="text-xs text-slate-500">{isAr ? 'اليوم' : 'Today'}</span>
            </div>
            <p className="text-2xl font-bold text-emerald-600">{summary.today_activities}</p>
          </div>
          <div className="bg-white dark:bg-slate-800 rounded-xl p-4 border border-slate-200 dark:border-slate-700">
            <div className="flex items-center gap-2 mb-1">
              <MapPin className="w-4 h-4 text-purple-500" />
              <span className="text-xs text-slate-500">{isAr ? 'زوار فريدين (اليوم)' : 'Unique IPs Today'}</span>
            </div>
            <p className="text-2xl font-bold text-purple-600">{summary.unique_visitors_today}</p>
          </div>
          <div className="bg-white dark:bg-slate-800 rounded-xl p-4 border border-slate-200 dark:border-slate-700">
            <div className="flex items-center gap-2 mb-1">
              <Globe className="w-4 h-4 text-amber-500" />
              <span className="text-xs text-slate-500">{isAr ? 'إجمالي الزوار' : 'Total Unique IPs'}</span>
            </div>
            <p className="text-2xl font-bold text-amber-600">{summary.unique_visitors_total}</p>
          </div>
        </div>
      )}

      {/* Top Actions Summary */}
      {summary?.top_actions && Object.keys(summary.top_actions).length > 0 && (
        <div className="bg-white dark:bg-slate-800 rounded-xl p-4 border border-slate-200 dark:border-slate-700">
          <h3 className="text-sm font-semibold mb-3 text-slate-700 dark:text-slate-300">{isAr ? 'أكثر الأنشطة' : 'Top Actions'}</h3>
          <div className="flex flex-wrap gap-2">
            {Object.entries(summary.top_actions).map(([action, count]) => {
              const info = getActionInfo(action);
              const Icon = info.icon;
              return (
                <span key={action} className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-slate-100 dark:bg-slate-700 text-xs">
                  <Icon className={`w-3 h-3 ${info.color}`} />
                  <span>{isAr ? info.label : info.labelEn}</span>
                  <span className="font-bold text-slate-900 dark:text-white">{count}</span>
                </span>
              );
            })}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-2 items-center">
        <div className="flex-1 min-w-[200px]">
          <Input
            placeholder={isAr ? 'بحث بالاسم، الإيميل، IP...' : 'Search name, email, IP...'}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="h-9"
            data-testid="activity-search"
          />
        </div>
        <Select value={filterAction} onValueChange={(v) => { setFilterAction(v); setPage(0); }}>
          <SelectTrigger className="w-[180px] h-9" data-testid="activity-filter">
            <SelectValue placeholder={isAr ? 'فلتر النشاط' : 'Filter Action'} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{isAr ? 'الكل' : 'All'}</SelectItem>
            {actionTypes.map(type => {
              const info = getActionInfo(type);
              return <SelectItem key={type} value={type}>{isAr ? info.label : info.labelEn}</SelectItem>;
            })}
          </SelectContent>
        </Select>
        <Button size="sm" variant="outline" onClick={() => { setLoading(true); fetchData(); }} data-testid="activity-refresh">
          <RefreshCw className="w-4 h-4" />
        </Button>
        <Button size="sm" variant="destructive" onClick={handleCleanup} data-testid="activity-cleanup">
          <Trash2 className="w-4 h-4 mr-1" />
          {isAr ? 'حذف' : 'Clear'}
        </Button>
      </div>

      {/* Activities Table */}
      <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 dark:bg-slate-900">
              <tr>
                <th className="px-3 py-2 text-right text-xs font-medium text-slate-500">{isAr ? 'النشاط' : 'Action'}</th>
                <th className="px-3 py-2 text-right text-xs font-medium text-slate-500">{isAr ? 'التفاصيل' : 'Details'}</th>
                <th className="px-3 py-2 text-right text-xs font-medium text-slate-500">{isAr ? 'المستخدم' : 'User'}</th>
                <th className="px-3 py-2 text-right text-xs font-medium text-slate-500">IP</th>
                <th className="px-3 py-2 text-right text-xs font-medium text-slate-500">{isAr ? 'اللغة' : 'Lang'}</th>
                <th className="px-3 py-2 text-right text-xs font-medium text-slate-500">{isAr ? 'الوقت' : 'Time'}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
              {filtered.length === 0 ? (
                <tr><td colSpan="6" className="px-3 py-8 text-center text-slate-400">{isAr ? 'لا توجد أنشطة' : 'No activities'}</td></tr>
              ) : filtered.map((a, idx) => {
                const info = getActionInfo(a.action);
                const Icon = info.icon;
                return (
                  <tr key={a.id || idx} className="hover:bg-slate-50 dark:hover:bg-slate-700/50">
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-1.5">
                        <Icon className={`w-4 h-4 ${info.color} flex-shrink-0`} />
                        <span className="text-xs font-medium">{isAr ? info.label : info.labelEn}</span>
                      </div>
                    </td>
                    <td className="px-3 py-2 text-xs text-slate-600 dark:text-slate-400 max-w-[200px] truncate">{a.details || a.page || '-'}</td>
                    <td className="px-3 py-2">
                      <div className="text-xs">
                        <div className="font-medium text-slate-900 dark:text-white">{a.user_name || (isAr ? 'زائر' : 'Guest')}</div>
                        {a.user_email && <div className="text-slate-400 text-[10px]">{a.user_email}</div>}
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      <button
                        onClick={() => { setFilterIP(a.ip_address); setPage(0); setLoading(true); }}
                        className="text-xs font-mono text-blue-500 hover:underline cursor-pointer"
                        title={isAr ? 'فلتر بهذا IP' : 'Filter by this IP'}
                      >
                        {a.ip_address || '-'}
                      </button>
                    </td>
                    <td className="px-3 py-2 text-xs text-slate-500">{a.language || '-'}</td>
                    <td className="px-3 py-2 text-xs text-slate-400 whitespace-nowrap">{formatTime(a.timestamp)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between text-sm text-slate-500">
        <span>{isAr ? `${total} نشاط` : `${total} activities`}</span>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" disabled={page === 0} onClick={() => { setPage(p => p - 1); setLoading(true); }}>
            {isAr ? 'السابق' : 'Prev'}
          </Button>
          <span className="flex items-center px-2">{page + 1} / {Math.max(1, Math.ceil(total / 50))}</span>
          <Button size="sm" variant="outline" disabled={(page + 1) * 50 >= total} onClick={() => { setPage(p => p + 1); setLoading(true); }}>
            {isAr ? 'التالي' : 'Next'}
          </Button>
        </div>
      </div>

      {/* IP Filter indicator */}
      {filterIP && (
        <div className="flex items-center gap-2 p-2 bg-blue-50 dark:bg-blue-950/30 rounded-lg text-sm">
          <MapPin className="w-4 h-4 text-blue-500" />
          <span>{isAr ? 'فلتر IP:' : 'Filtering IP:'} <strong>{filterIP}</strong></span>
          <Button size="sm" variant="ghost" className="h-6 px-2" onClick={() => { setFilterIP(''); setPage(0); setLoading(true); }}>✕</Button>
        </div>
      )}
    </div>
  );
};

export default ActivityLogs;
